import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import isApiError from "@insite/client-framework/Common/isApiError";
import { addToChainAfter, executeAwaitableHandlerChain, Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { get, ApiParameter } from "@insite/client-framework/Services/ApiService";
import { getProductByPath, GetProductByPathApiV2Parameter } from "@insite/client-framework/Services/ProductServiceV2";
import { StorefrontAccess } from "@insite/client-framework/Services/SettingsService";
import { recentlyViewedGetProductCollectionParameter } from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadProductByPath from "@insite/client-framework/Store/Data/Products/Handlers/LoadProductByPath";
import {
    chain as loadProductByPathChain,
    RequestDataFromApi,
} from "@insite/client-framework/Store/Data/Products/Handlers/LoadProductByPath";
import {
    chain,
    DispatchBeginLoadProduct,
    DispatchCompleteLoadProduct,
    LoadProductIfNeeded,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/DisplayProduct";
import { CatalogPageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { getRawSelectionsFromUrl } from "@tSurya/Handlers/Configurator/ConfiguratorUrlUtils";
import initializeConfigurator from "@tSurya/Handlers/Configurator/InitializeConfigurator";

type Parameter = {
    path: string;
    styledOption?: string;
};
type Props = {
    product?: ProductModel;
    productInfosById?: SafeDictionary<ProductInfo>;
    variantChildren?: ProductModel[];
    variantChildrenProductInfos?: ProductInfo[];
    variantSelection?: SafeDictionary<string>;
    pricingLoaded?: true;
    inventoryLoaded?: true;
};

export const LoadProductIfNeededCustom: HandlerType = async props => {
    // if variantTypeId is not null this has variantTraits, and we need those loaded fully for this handler chain to work correctly
    if (props.product && props.product.variantTypeId === null) {
        if (
            !props.product.detail ||
            !props.product.content ||
            !props.product.images ||
            !props.product.documents ||
            !props.product.specifications ||
            !props.product.attributeTypes ||
            !props.product.variantTraits
        ) {
            // we don't want to wait for this if we already partially have the product, because then we can't show the partially loaded product page
            props.dispatch(
                loadProductByPath({
                    ...props.parameter,
                    addToRecentlyViewed: true,
                    onComplete: productProps => {
                        const configuration = productProps?.apiResult?.detail?.configuration;
                        if (configuration) {
                            props.dispatch({
                                type: "Pages/ProductDetails/InitConfigurationSelection",
                                configuration,
                            });
                        }

                        props.dispatch({
                            type: "Data/Products/ReplaceDataView",
                            parameter: recentlyViewedGetProductCollectionParameter,
                            dataView: {},
                        });
                    },
                }),
            );
        } else {
            getProductByPath({
                ...props.parameter,
                addToRecentlyViewed: true,
                expand: ["attributes", "properties"],
            }).then(result => {
                props.dispatch({
                    type: "Data/Products/CompleteLoadProduct",
                    model: result,
                });
            });
        }

        return;
    }

    props.product = await executeAwaitableHandlerChain(
        loadProductByPath,
        { ...props.parameter, addToRecentlyViewed: true },
        props,
    );
};

type HandlerType = Handler<Parameter, Props>;

replaceInChain(chain, LoadProductIfNeeded, LoadProductIfNeededCustom);

export const ResetConfigurator: HandlerType = props => {
    props.dispatch({ type: "Custom/Configurator/Reset" });
};

addToChainAfter(chain, DispatchBeginLoadProduct, ResetConfigurator);

export const InitializeConfiguratorFromProduct: HandlerType = async props => {
    if (!props.product) {
        return;
    }
    await props.dispatch(initializeConfigurator({ product: props.product }));
};

addToChainAfter(chain, DispatchCompleteLoadProduct, InitializeConfiguratorFromProduct);

// Override RequestDataFromApi in the loadProductByPath chain to pass
// configurator URL selections as a query parameter on the product GET.
// This lets MgbwPopulateConfiguratorData compute the correct configurator
// state on the initial product load, which is critical for SSR.
const RequestDataFromApiWithSelections = async (props: any) => {
    var param = props.apiParameter as GetProductByPathApiV2Parameter;
    var urlSelections = getRawSelectionsFromUrl();
    var mgbwSelections =
        Object.keys(urlSelections).length > 0 ? JSON.stringify(urlSelections) : undefined;

    try {
        var catalogResult = await get<CatalogPageModel>("api/v1/catalogpages", {
            path: param.path,
        } as ApiParameter);
        props.apiResult = await get<ProductModel>(
            `api/v2/products/${catalogResult.productId}`,
            {
                expand: param.expand,
                additionalExpands: (param as any).additionalExpands,
                includeAttributes: (param as any).includeAttributes,
                addToRecentlyViewed: param.addToRecentlyViewed,
                categoryId: catalogResult.category?.id,
                mgbwSelections,
            } as ApiParameter,
        );
    } catch (error: any) {
        if (isApiError(error) && error.status === 400) {
            props.parameter.onError?.(error.errorJson.message);
            return false;
        }

        var { storefrontAccess } = getSettingsCollection(props.getState()).productSettings;
        if (storefrontAccess === StorefrontAccess.SignInRequiredToBrowse && error.status === 403) {
            error.status = 401;
            error.errorJson = { message: "error" };
        }

        throw error;
    }
};

replaceInChain(loadProductByPathChain, RequestDataFromApi, RequestDataFromApiWithSelections);
